import { ContextMenu as ContextMenuPrimitive } from "@base-ui/react/context-menu";
import { clsx } from "clsx";
import { FA6Icon } from "./FA6Icon";
import styles from "./Menu.module.css";
import contextStyles from "./ContextMenu.module.css";

function ContextMenuRoot(props: ContextMenuPrimitive.Root.Props) {
	return <ContextMenuPrimitive.Root {...props} />;
}

function ContextMenuTrigger({ className, ...props }: ContextMenuPrimitive.Trigger.Props) {
	return (
		<ContextMenuPrimitive.Trigger
			data-slot="context-menu-trigger"
			className={clsx(contextStyles.trigger, className)}
			{...props}
		/>
	);
}

// Bundles Portal + Positioner + Popup. Works unchanged for the top-level menu
// (cursor-anchored) and submenus (Base UI flips them to the inline-end).
function ContextMenuPopup({
	className,
	children,
	side,
	align = "start",
	sideOffset,
	alignOffset,
	...props
}: ContextMenuPrimitive.Popup.Props &
	Pick<ContextMenuPrimitive.Positioner.Props, "side" | "align" | "sideOffset" | "alignOffset">) {
	return (
		<ContextMenuPrimitive.Portal data-slot="context-menu-portal">
			<ContextMenuPrimitive.Positioner
				data-slot="context-menu-positioner"
				className={styles.positioner}
				side={side}
				align={align}
				sideOffset={sideOffset}
				alignOffset={alignOffset}
			>
				<ContextMenuPrimitive.Popup data-slot="context-menu-popup" className={clsx(styles.popup, className)} {...props}>
					{children}
				</ContextMenuPrimitive.Popup>
			</ContextMenuPrimitive.Positioner>
		</ContextMenuPrimitive.Portal>
	);
}

function ContextMenuItem({ className, ...props }: ContextMenuPrimitive.Item.Props) {
	return <ContextMenuPrimitive.Item data-slot="context-menu-item" className={clsx(styles.item, className)} {...props} />;
}


function ContextMenuLinkItem({ className, closeOnClick = true, ...props }: ContextMenuPrimitive.LinkItem.Props) {
	return (
		<ContextMenuPrimitive.LinkItem
			data-slot="context-menu-link-item"
			className={clsx(styles.item, styles.linkItem, className)}
			closeOnClick={closeOnClick}
			{...props}
		/>
	);
}

function ContextMenuSeparator({ className, ...props }: ContextMenuPrimitive.Separator.Props) {
	return (
		<ContextMenuPrimitive.Separator
			data-slot="context-menu-separator"
			className={clsx(styles.separator, className)}
			{...props}
		/>
	);
}

function ContextMenuGroup({ className, ...props }: ContextMenuPrimitive.Group.Props) {
	return <ContextMenuPrimitive.Group data-slot="context-menu-group" className={className} {...props} />;
}

function ContextMenuGroupLabel({ className, ...props }: ContextMenuPrimitive.GroupLabel.Props) {
	return (
		<ContextMenuPrimitive.GroupLabel
			data-slot="context-menu-group-label"
			className={clsx(styles.groupLabel, className)}
			{...props}
		/>
	);
}

// Keeps the menu open on click by default. The indicator slot reserves its width.
function ContextMenuCheckboxItem({ className, children, ...props }: ContextMenuPrimitive.CheckboxItem.Props) {
	return (
		<ContextMenuPrimitive.CheckboxItem
			data-slot="context-menu-checkbox-item"
			className={clsx(styles.item, className)}
			{...props}
		>
			<span className={styles.indicator}>
				<ContextMenuPrimitive.CheckboxItemIndicator data-slot="context-menu-checkbox-item-indicator">
					<FA6Icon icon="check" />
				</ContextMenuPrimitive.CheckboxItemIndicator>
			</span>
			<span className={styles.itemText}>{children}</span>
		</ContextMenuPrimitive.CheckboxItem>
	);
}

function ContextMenuRadioGroup({ className, ...props }: ContextMenuPrimitive.RadioGroup.Props) {
	return <ContextMenuPrimitive.RadioGroup data-slot="context-menu-radio-group" className={className} {...props} />;
}

function ContextMenuRadioItem({ className, children, ...props }: ContextMenuPrimitive.RadioItem.Props) {
	return (
		<ContextMenuPrimitive.RadioItem data-slot="context-menu-radio-item" className={clsx(styles.item, className)} {...props}>
			<span className={styles.indicator}>
				<ContextMenuPrimitive.RadioItemIndicator data-slot="context-menu-radio-item-indicator">
					<FA6Icon icon="circle" className={styles.radioDot} />
				</ContextMenuPrimitive.RadioItemIndicator>
			</span>
			<span className={styles.itemText}>{children}</span>
		</ContextMenuPrimitive.RadioItem>
	);
}

function ContextMenuSubmenuRoot(props: ContextMenuPrimitive.SubmenuRoot.Props) {
	return <ContextMenuPrimitive.SubmenuRoot {...props} />;
}

// The entry that opens a submenu — an item with a trailing chevron.
function ContextMenuSubmenuTrigger({ className, children, ...props }: ContextMenuPrimitive.SubmenuTrigger.Props) {
	return (
		<ContextMenuPrimitive.SubmenuTrigger
			data-slot="context-menu-submenu-trigger"
			className={clsx(styles.item, styles.submenuTrigger, className)}
			{...props}
		>
			<span className={styles.itemText}>{children}</span>
			<FA6Icon icon="angle-right" className={styles.submenuChevron} />
		</ContextMenuPrimitive.SubmenuTrigger>
	);
}

const ContextMenu = {
	Root: ContextMenuRoot,
	Trigger: ContextMenuTrigger,
	Popup: ContextMenuPopup,
	Item: ContextMenuItem,
	LinkItem: ContextMenuLinkItem,
	Separator: ContextMenuSeparator,
	Group: ContextMenuGroup,
	GroupLabel: ContextMenuGroupLabel,
	CheckboxItem: ContextMenuCheckboxItem,
	RadioGroup: ContextMenuRadioGroup,
	RadioItem: ContextMenuRadioItem,
	SubmenuRoot: ContextMenuSubmenuRoot,
	SubmenuTrigger: ContextMenuSubmenuTrigger,
};

export { ContextMenu };
